import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Grid, Button, TextField, MenuItem, InputAdornment, CircularProgress, Snackbar,
} from '@material-ui/core'
import { Add, Search } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { uniqBy } from 'lodash'
import PageTitle from 'components/PageTitle'
import { getDadosUsuario } from 'store/modules/auth/auth.selectors'
import { TIPO_USUARIO } from 'utils/constantes'
import { getTitulos, excluirTitulo, getEmpresas } from 'store/modules/api/titulos-lbc/titulos-lbc.api'
import { transferir } from 'store/modules/api/operacoes-lbc/operacoes-lbc.api'
import StatCards from './components/stat-cards/stat-cards'
import Lista from './components/lista/lista'
import Form from './components/form/form'
import TransferirModal from './components/transferir-modal'
import { CadeiaDialog } from './components/cadeia'
import { anosBase } from './titulos-lbc.helpers'

const anos = anosBase()

export const TitulosLbc = () => {
  const { t } = useTranslation()
  const usuario = useSelector(getDadosUsuario)
  const podeEditar = usuario?.tipoUsuario?.id === TIPO_USUARIO.CLIENTE.GERENTE_CREDITO
  const ehCliente = !podeEditar
    && usuario?.tipoUsuario?.id !== TIPO_USUARIO.CLIENTE.OBSERVADOR_CREDITO

  const [titulos, setTitulos] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtros, setFiltros] = useState({ anoBase: '', status: '', ecorregiaoId: '', empresaGeradoraId: '', empresaDestinatariaId: '' })
  const [busca, setBusca] = useState('')
  const [formAberto, setFormAberto] = useState(false)
  const [selecionado, setSelecionado] = useState(null)
  const [erro, setErro] = useState('')
  const [empresas, setEmpresas] = useState([])
  const [transferindo, setTransferindo] = useState(null)
  const [cadeiaId, setCadeiaId] = useState(null)

  const carregar = useCallback(async () => {
    setLoading(true)
    try {
      setTitulos(await getTitulos(filtros))
    } catch (e) {
      setErro(e.message)
    } finally {
      setLoading(false)
    }
  }, [filtros])

  useEffect(() => { carregar() }, [carregar])

  useEffect(() => {
    if (!podeEditar) return
    getEmpresas().then(setEmpresas).catch(() => setEmpresas([]))
  }, [podeEditar])

  const confirmarTransferencia = async ({ empresaDestinatariaNovaId, quantidade }) => {
    try {
      const novo = await transferir({ tituloOrigemId: transferindo.id, empresaDestinatariaNovaId, quantidade })
      setTransferindo(null)
      await carregar()
      setErro(t('operacoesLbc.transferenciaOk', { codigo: novo?.codigo || '' }))
    } catch (e) {
      setErro(e.message)
    }
  }

  const ecorregioes = useMemo(
    () => uniqBy(titulos.map((x) => x.ecorregiao).filter(Boolean), 'id'),
    [titulos],
  )

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase()
    if (!q) return titulos
    return titulos.filter((x) => (x.codigo || '').toLowerCase().includes(q))
  }, [titulos, busca])

  const novo = () => { setSelecionado(null); setFormAberto(true) }
  const editar = (tit) => { setSelecionado(tit); setFormAberto(true) }

  const excluir = async (tit) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('titulosLbc.confirmarExclusao', { codigo: tit.codigo }))) return
    try {
      await excluirTitulo(tit.id)
      await carregar()
    } catch (e) {
      setErro(e.message)
    }
  }

  const setFiltro = (campo) => (e) => setFiltros((f) => ({ ...f, [campo]: e.target.value }))

  return (
    <>
      <PageTitle title={ehCliente ? t('titulosLbc.tituloGestao') : t('titulosLbc.titulo')}>
        {podeEditar && (
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={novo}>
            {t('titulosLbc.novo')}
          </Button>
        )}
      </PageTitle>

      <StatCards titulos={titulos} />

      <Grid container spacing={2} style={{ marginTop: 4, marginBottom: 12 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth size="small" label={t('titulosLbc.buscarCodigo')}
            value={busca} onChange={(e) => setBusca(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
        </Grid>
        {podeEditar && (
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label={t('titulosLbc.geradora')} value={filtros.empresaGeradoraId} onChange={setFiltro('empresaGeradoraId')}>
              <MenuItem value="">{t('titulosLbc.todos')}</MenuItem>
              {empresas.map((emp) => <MenuItem key={emp.id} value={emp.id}>{emp.nome}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        {podeEditar && (
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label={t('titulosLbc.destinataria')} value={filtros.empresaDestinatariaId} onChange={setFiltro('empresaDestinatariaId')}>
              <MenuItem value="">{t('titulosLbc.todos')}</MenuItem>
              {empresas.map((emp) => <MenuItem key={emp.id} value={emp.id}>{emp.nome}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        <Grid item xs={6} sm={3}>
          <TextField select fullWidth size="small" label={t('titulosLbc.ecorregiao')} value={filtros.ecorregiaoId} onChange={setFiltro('ecorregiaoId')}>
            <MenuItem value="">{t('titulosLbc.todos')}</MenuItem>
            {ecorregioes.map((eco) => <MenuItem key={eco.id} value={eco.id}>{eco.nome}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField select fullWidth size="small" label={t('titulosLbc.anoBase')} value={filtros.anoBase} onChange={setFiltro('anoBase')}>
            <MenuItem value="">{t('titulosLbc.todos')}</MenuItem>
            {anos.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField select fullWidth size="small" label={t('titulosLbc.status')} value={filtros.status} onChange={setFiltro('status')}>
            <MenuItem value="">{t('titulosLbc.todos')}</MenuItem>
            {['active', 'retired', 'exhausted', 'suspended'].map((s) => (
              <MenuItem key={s} value={s}>{t(`titulosLbc.statusValor.${s}`)}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {loading ? <CircularProgress /> : (
        <Lista
          titulos={visiveis}
          podeEditar={podeEditar}
          onEditar={editar}
          onExcluir={excluir}
          onTransferir={setTransferindo}
          onVerCadeia={(tit) => setCadeiaId(tit.id)}
        />
      )}

      {formAberto && (
        <Form
          aberto={formAberto}
          titulo={selecionado}
          onFechar={() => setFormAberto(false)}
          onSalvo={() => { setFormAberto(false); carregar() }}
          onErro={setErro}
        />
      )}

      {transferindo && (
        <TransferirModal
          titulo={transferindo}
          empresas={empresas}
          onFechar={() => setTransferindo(null)}
          onConfirmar={confirmarTransferencia}
        />
      )}

      {cadeiaId && <CadeiaDialog tituloId={cadeiaId} onFechar={() => setCadeiaId(null)} />}

      <Snackbar open={!!erro} autoHideDuration={6000} onClose={() => setErro('')} message={erro} />
    </>
  )
}

export default TitulosLbc
